import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Collapse, Dropdown, Spinner } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { logout, acknowledgeSessionExpired } from './store/authSlice';
import { wakeAuthApi } from './services/authService';
import ModeSelector from './components/ModeSelector';
import TrackSelector from './components/TrackSelector';
import BackingTrackSettings from './components/BackingTrackSettings';
import MetronomeSettings from './components/MetronomeSettings';
import Player from './components/Player';
import CountdownTimer from './components/CountdownTimer';
import TrackLinks from './components/TrackLinks';
import TrackEditor from './components/TrackEditor';
import PracticeRecorder from './components/PracticeRecorder';
import PracticeStats from './components/PracticeStats';
import AuthModal from './components/AuthModal';
import { Track } from './types';
import { loadTracks } from './utils/trackLoader';
import './App.css';

function App() {
  const dispatch = useAppDispatch();
  const mode = useAppSelector((state) => state.app.mode);
  const selectedTrackId = useAppSelector((state) => state.app.backingTrack.selectedTrackId);
  const selectedPartIndex = useAppSelector((state) => state.app.backingTrack.selectedPartIndex);
  const { isAuthenticated, username, emailConfirmPending, sessionExpired } = useAppSelector((state) => state.auth);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [tracksLoading, setTracksLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMessage, setAuthModalMessage] = useState<string | null>(null);
  const [authModalTab, setAuthModalTab] = useState<'login' | 'register' | 'forgot' | 'confirm'>('login');
  const [showInstructions, setShowInstructions] = useState(false);
  const [trackEditorDirty, setTrackEditorDirty] = useState(false);

  const guardModeChange = useCallback(() => {
    if (mode === 'tracks' && trackEditorDirty) {
      return window.confirm(
        'You have unsaved track changes. Leave anyway? Your changes will be lost.',
      );
    }
    return true;
  }, [mode, trackEditorDirty]);

  const reloadTracks = useCallback(() => {
    setTracksLoading(true);
    loadTracks()
      .then(setTracks)
      .catch(() => {})
      .finally(() => setTracksLoading(false));
  }, []);

  useEffect(() => {
    reloadTracks();
  }, [isAuthenticated, reloadTracks]);

  // Ping the auth API once on first load so a cold Fly.io machine is already
  // starting up by the time the user tries to sign in.
  useEffect(() => {
    wakeAuthApi();
  }, []);

  // Refresh token turned out to be expired/invalid — pop the login screen
  // back up instead of silently leaving the user signed out.
  useEffect(() => {
    if (sessionExpired) {
      setAuthModalMessage('Your session has expired. Please sign in again.');
      setAuthModalTab('login');
      setShowAuthModal(true);
      dispatch(acknowledgeSessionExpired());
    }
  }, [sessionExpired, dispatch]);

  const handleRequireLogin = useCallback((message: string) => {
    setAuthModalMessage(message);
    setShowAuthModal(true);
  }, []);

  const handleHideAuthModal = useCallback(() => {
    setShowAuthModal(false);
    setAuthModalMessage(null);
  }, []);

  return (
    <div className="App">
      <Container className="py-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h1 className="mb-0">
            <span className="eq" aria-hidden="true">
              <span /><span /><span /><span /><span />
            </span>
            RiffRacer{/* (or Riffaroo, Riffi, Riffly, LoopHero, BeatChamp, ) */}
          </h1>
          <div className="d-flex align-items-center gap-2">
              {isAuthenticated ? (
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    size="sm"
                    id="profile-menu"
                    className="profile-menu-toggle d-flex align-items-center gap-2"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
                    </svg>
                    <span>{username}</span>
                    {emailConfirmPending && (
                      <Badge bg="warning" text="dark">Confirm email</Badge>
                    )}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="dark-select-menu">
                    {emailConfirmPending && (
                      <>
                        <Dropdown.Item
                          onClick={() => {
                            setAuthModalMessage(null);
                            setAuthModalTab('confirm');
                            setShowAuthModal(true);
                          }}
                        >
                          Confirm Email
                        </Dropdown.Item>
                        <Dropdown.Divider />
                      </>
                    )}
                    <Dropdown.Item
                      onClick={() => {
                        setAuthModalMessage(null);
                        setAuthModalTab('forgot');
                        setShowAuthModal(true);
                      }}
                    >
                      Change Password
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => dispatch(logout())}>
                      Sign Out
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => { setAuthModalMessage(null); setAuthModalTab('login'); setShowAuthModal(true); }}
                >
                  Sign In
                </Button>
              )}
            </div>
        </div>

        {tracksLoading && (
          <Alert variant="info" className="mb-3 d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Loading tracks…</span>
          </Alert>
        )}

        {emailConfirmPending && (
          <Alert variant="warning" className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span>
              We've sent a 6-digit confirmation code to your email. Please enter it to confirm
              your account.
            </span>
            <Button
              variant="warning"
              size="sm"
              onClick={() => {
                setAuthModalMessage(null);
                setAuthModalTab('confirm');
                setShowAuthModal(true);
              }}
            >
              Enter code
            </Button>
          </Alert>
        )}

        <Row
          className="justify-content-center"
          style={tracksLoading ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
          aria-busy={tracksLoading}
        >
          <Col md={8} lg={6}>
            <Card className="shadow-sm mb-4" style={{ position: 'relative', zIndex: 2 }}>
              <Card.Body>
                <ModeSelector beforeChange={guardModeChange} />

                {mode === 'backingTrack' && (
                  <>
                    <div className="mb-3">
                      <small className="text-muted form-text">Select a track and song part to practice. The app will loop the selected part.</small>
                    </div>
                    <TrackSelector tracks={tracks} />
                  </>
                )}

                {mode === 'metronome' && (
                  <>
                    <div className="mb-3">
                      <small className="text-muted form-text">Practice with a click track that can auto-increase after a set number of beats.</small>
                    </div>
                  </>
                )}

                {mode !== 'tracks' && mode !== 'stats' && !(mode === 'backingTrack' && !selectedTrackId) && (
                  <>
                    <CountdownTimer />
                    <Player tracks={tracks} />
                    <PracticeRecorder />
                  </>
                )}

                {mode === 'metronome' && (
                  <MetronomeSettings />
                )}

                {mode === 'backingTrack' && selectedTrackId && (
                  <>
                    <hr className="my-4" />
                    <BackingTrackSettings
                      isYouTubeTrack={!!tracks.find((t) => t.id === selectedTrackId)?.youtubeId}
                      trackBpm={(() => {
                        const track = tracks.find((t) => t.id === selectedTrackId);
                        if (!track) return undefined;
                        const part = selectedPartIndex !== null ? track.metadata.parts[selectedPartIndex] : undefined;
                        return part?.bpmOverride ?? track.metadata.bpm;
                      })()}
                    />
                    <TrackLinks tracks={tracks} />
                  </>
                )}

                {mode === 'tracks' && (
                  <TrackEditor
                    onTracksChanged={reloadTracks}
                    onRequireLogin={handleRequireLogin}
                    onDirtyChange={setTrackEditorDirty}
                  />
                )}

                {mode === 'stats' && <PracticeStats />}
              </Card.Body>
            </Card>

            {false ?? (<Card className="shadow-sm bg-light instructions-panel" style={{ position: 'relative', zIndex: 1 }}>
              <Card.Body>
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none d-flex align-items-center justify-content-between w-100 mb-3"
                  onClick={() => setShowInstructions((v) => !v)}
                  aria-expanded={showInstructions}
                  aria-controls="instructions-collapse"
                  style={{ color: 'inherit' }}
                >
                  <h5 className="mb-0">Instructions</h5>
                  <span aria-hidden="true" style={{ transition: 'transform 0.2s', transform: showInstructions ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                    ▶
                  </span>
                </button>
                <Collapse in={showInstructions}>
                  <div id="instructions-collapse">
                    <ul className="small mb-0 mt-3">
                      <li><strong>Backing Track Mode:</strong> Select a track and song part to practice. The app will loop the selected part.</li>
                      <li><strong>Auto Speed Up:</strong> Gradually increases tempo after each loop until target BPM is reached.</li>
                      <li><strong>Pitch Shift:</strong> Adjust pitch without changing tempo (useful for songs tuned differently).</li>
                      <li><strong>Metronome Mode:</strong> Practice with a click track that can auto-increase after a set number of beats.</li>
                    </ul>
                  </div>
                </Collapse>
              </Card.Body>
            </Card>)}
          </Col>
        </Row>

      </Container>

      <footer className="app-footer">
        <Container className="py-3 text-center small text-muted">
          © riffracer — <a href="mailto:hello@riffracer.co.uk">hello@riffracer.co.uk</a>
        </Container>
      </footer>

      <AuthModal show={showAuthModal} onHide={handleHideAuthModal} message={authModalMessage} initialTab={authModalTab} />
    </div>
  );
}

export default App;
